import { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ar from "./ar.json";
import en from "./en.json";

const DICTS = { ar, en };
export const LANGS = ["ar", "en"];

const I18nContext = createContext({ lang: "ar", t: (k) => k, switchLang: () => {} });

function getByPath(obj, path) {
  return path.split(".").reduce((acc, p) => (acc && acc[p] !== undefined ? acc[p] : undefined), obj);
}

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const lang = useMemo(() => {
    const seg = location.pathname.split("/").filter(Boolean)[0];
    return LANGS.includes(seg) ? seg : "ar";
  }, [location.pathname]);

  const t = useMemo(() => {
    const dict = DICTS[lang];
    return (key, fallback = "") => {
      const val = getByPath(dict, key);
      return val === undefined ? fallback || key : val;
    };
  }, [lang]);

  const switchLang = (newLang) => {
    if (!LANGS.includes(newLang) || newLang === lang) return;
    const parts = location.pathname.split("/").filter(Boolean);
    // Legal route mapping across languages
    const legalMap = {
      ar: { privacy: "privacidad", cookies: "cookies", terms: "terminos" },
      en: { privacy: "privacy", cookies: "cookies", terms: "terms" },
    };
    const reverse = {
      privacidad: "privacy",
      privacy: "privacy",
      cookies: "cookies",
      terminos: "terms",
      terms: "terms",
    };

    // Preserve legal page if we're on one
    let target = `/${newLang}`;
    if (parts.length >= 2 && LANGS.includes(parts[0])) {
      const kind = reverse[parts[1]];
      if (kind) target = `/${newLang}/${legalMap[newLang][kind]}`;
    }
    // Preserve hash for anchors
    navigate(target + (location.hash || ""), { state: { skipScrollTop: true, langSwitch: true } });
  };

  return (
    <I18nContext.Provider value={{ lang, t, switchLang }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
